"use client";

import { useEffect, useRef, useState, useSyncExternalStore } from "react";
import type { Heading } from "@/lib/md";

/** A thin ink line along the top edge that fills as the post is read. */
export function ReadingProgress() {
  const barRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const body = document.querySelector("[data-post-body]");
    if (!body) return;
    const onScroll = () => {
      const r = body.getBoundingClientRect();
      const total = Math.max(1, r.height - window.innerHeight * 0.6);
      const p = Math.min(1, Math.max(0, -r.top / total));
      if (barRef.current) barRef.current.style.transform = `scaleX(${p})`;
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, []);

  return (
    <div className="progress" aria-hidden="true">
      <i ref={barRef} />
    </div>
  );
}

/** In-page contents built from the post's headings; the one being read is marked. */
export function Toc({ headings }: { headings: Heading[] }) {
  const [active, setActive] = useState<string | undefined>(headings[0]?.id);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        for (const e of entries) if (e.isIntersecting) setActive(e.target.id);
      },
      { rootMargin: "-15% 0px -70% 0px" },
    );
    for (const h of headings) {
      const el = document.getElementById(h.id);
      if (el) observer.observe(el);
    }
    return () => observer.disconnect();
  }, [headings]);

  if (headings.length === 0) return null;
  return (
    <nav className="toc" aria-label="In this post">
      <p className="label">In this post</p>
      <ul>
        {headings.map((h) => (
          <li key={h.id}>
            <a href={`#${h.id}`} className={h.level === 3 ? "sub" : undefined} aria-current={active === h.id ? "true" : undefined}>
              {h.text}
            </a>
          </li>
        ))}
      </ul>
    </nav>
  );
}

const noSubscribe = () => () => {};

export function ShareRow({ url, title }: { url: string; title: string }) {
  const [copied, setCopied] = useState(false);
  // the phone share sheet, where there is one
  const native = useSyncExternalStore(noSubscribe, () => typeof navigator.share === "function", () => false);
  const u = encodeURIComponent(url);
  const t = encodeURIComponent(title);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    } catch {
      // clipboard unavailable: the share links still work
    }
  };

  return (
    <div>
      <p className="label">Share</p>
      <div className="share-row">
        <button type="button" onClick={copy} className="chip">
          {copied ? "Copied" : "Copy link"}
        </button>
        {native && (
          <button type="button" className="chip" onClick={() => navigator.share({ title, url }).catch(() => {})}>
            Share…
          </button>
        )}
        <a className="chip" href={`https://www.linkedin.com/sharing/share-offsite/?url=${u}`} target="_blank" rel="noopener noreferrer">
          LinkedIn
        </a>
        <a className="chip" href={`https://x.com/intent/post?text=${t}&url=${u}`} target="_blank" rel="noopener noreferrer">
          X
        </a>
        <a className="chip" href={`https://www.reddit.com/submit?url=${u}&title=${t}`} target="_blank" rel="noopener noreferrer">
          Reddit
        </a>
      </div>
    </div>
  );
}
